import { fetchAPI } from '@/api';
import Layout from '@/components/ux/Layout';
import SettingsToggle from '@/components/ux/SettingsToggle';
import { capturePosthogEvent, utils } from '@/helpers';
import { getEffectiveTimezoneAbbr } from '@/helpers/timezone';
import { Setter } from '@/legacy-types';
import { Button, DropdownSelect, Flex, FormDateInput, SlideOutPanel, SlideOutPanelHeader, Typography, useToast } from '@alpine-iq/ui-library';
import React from 'react';
import { create } from 'zustand';

type EmailReportReq = {
	emails: string[];
	start: number;
	end: number;
	optIn?: boolean; // Will return personas who are opted in
	optOut?: boolean; // Will return personas who are opted out
};

const initState = (): EmailReportReq => ({
	emails: [utils.auth.getEmail()] as string[],
	start: -1,
	end: -1,
});

type MessageReportStore = {
	open: boolean;
	campaignID: string | undefined;
	setOpen: (campaignID: string) => void;
	close: () => void;

	body: Partial<EmailReportReq>;
	setBody: (body: Setter<Partial<EmailReportReq>>) => void;
};

export const useMessageReportStore = create<MessageReportStore>()((set, get) => ({
	open: false,
	campaignID: undefined,
	setOpen: (campaignID: string) => set({ open: true, campaignID }),
	close: () => set({ open: false, campaignID: undefined }),

	body: initState(),
	setBody: (body: Setter<Partial<EmailReportReq>>) => {
		const current = get().body;
		const next = typeof body === 'function' ? body(current) : body;
		set({ body: next });
	},
}));

export default function MessageReportModal() {
	const { addToast } = useToast();
	const [loading, setLoading] = React.useState(false);
	const { open, campaignID, close, body, setBody } = useMessageReportStore();

	const timezoneText = getEffectiveTimezoneAbbr();

	const sendReport = async () => {
		setLoading(true);

		await fetchAPI(`/campaign/report/:uid/${campaignID}`, {
			method: 'PUT',
			payload: body,
		});

		addToast({
			message: 'Report will be emailed once processed.',
			type: 'success',
		});

		capturePosthogEvent('Campaign - Send Message Report', {
			campaignID: campaignID,
			emails: body.emails,
			optIn: body.optIn,
			optOut: body.optOut,
			start: body.start,
			end: body.end,
		});

		closeModal();
	};

	const closeModal = () => {
		setBody(initState());
		setLoading(false);
		close();
	};

	return (
		<>
			<SlideOutPanel
				open={open}
				onClose={closeModal}
			>
				<SlideOutPanelHeader
					onClose={closeModal}
					centerContent={<Typography variant="h3">Create Report</Typography>}
					actions={
						<Button
							loading={loading}
							variant="primary"
							onClick={sendReport}
						>
							Send Report
						</Button>
					}
				/>

				<Layout>
					<Flex
						column
						gap="24"
						padding="40"
					>
						<Typography variant="h2">Send a report via email</Typography>
						<Typography style={{ color: 'var(--text-text-secondary, #626875) ' }}>
							This report will be sent via email as soon as it's processed. It will contain a link to download a CSV attachment with the following columns of data by
							the date range of your choosing:
						</Typography>

						<Flex
							flex
							gap="24"
						>
							<Flex
								flex
								column
							>
								<FormDateInput
									loading={loading}
									label={`Start Date (${timezoneText})`}
									enableTime
									style={{ minWidth: '100%' }}
									value={(() => {
										const start = body.start;
										if (start === -1) return undefined;
										return start ? new Date(start * 1000).toISOString() : undefined;
									})()}
									onChange={(date) => {
										const start = date ? Math.floor(new Date(date as string).getTime() / 1000) : undefined;

										// Do not allow seletion of dates in the past
										if (start && start < Math.floor(Date.now() / 1000)) {
											return;
										}

										setBody((body) => ({ ...body, start }));
									}}
								/>
							</Flex>
							<Flex
								flex
								column
							>
								<FormDateInput
									loading={loading}
									label={`End Date (${timezoneText})`}
									enableTime
									style={{ minWidth: '100%' }}
									value={(() => {
										const end = body.end;
										if (end === -1) return undefined;
										return end ? new Date(end * 1000).toISOString() : undefined;
									})()}
									onChange={(date) => {
										const end = date ? Math.floor(new Date(date as string).getTime() / 1000) : undefined;

										// Do not allow seletion of dates in the past
										if (end && end < Math.floor(Date.now() / 1000)) {
											return;
										}

										setBody((body) => ({ ...body, end }));
									}}
								/>
							</Flex>
						</Flex>
						<SettingsToggle
							reversed
							disabled={loading}
							toggled={body.optIn}
							label="Only include personas who are Opted-In?"
							onChange={(optIn) => setBody((body) => ({ ...body, optIn }))}
						/>
						<SettingsToggle
							reversed
							disabled={loading}
							toggled={body.optOut}
							label="Only include personas who are Opted-Out?"
							onChange={(optOut) => setBody((body) => ({ ...body, optOut }))}
						/>

						<DropdownSelect<string>
							mode="multi"
							loading={loading}
							label="Send this report to"
							value={body.emails}
							options={body.emails?.map((email) => ({ label: email, value: email })) ?? []}
							onChange={(emails) => setBody((body) => ({ ...body, emails }))}
						/>
					</Flex>
				</Layout>
			</SlideOutPanel>
		</>
	);
}
